import {
  Button,
  Chip,
  Divider,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  User,
  addToast,
} from '@heroui/react';
import { t } from 'i18next';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  useApproveOrderMutation,
  useGetApprovalOrderByIdQuery,
  useRejectOrderMutation,
} from 'src/services/orders/orders';
import PdfViewer from 'src/components/UI/PdfViewer';
import { formatText } from 'src/utils/stringUtils';
import { getStatusChipStyle } from 'src/utils/orders';

interface ReviewOrderProps {
  orderId: string | null;
  onCloseModal: () => void;
  onActionComplete: () => void;
}

const ReviewOrder: React.FC<ReviewOrderProps> = ({
  orderId,
  onCloseModal,
  onActionComplete,
}) => {
  const {
    data: order,
    isLoading,
    error,
  } = useGetApprovalOrderByIdQuery(orderId as string, {
    skip: !orderId,
  });

  const [approveOrder, { isLoading: isApproving }] =
    useApproveOrderMutation();
  const [rejectOrder, { isLoading: isRejecting }] =
    useRejectOrderMutation();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleApproveClick = () => setIsApproveModalOpen(true);
  const handleRejectClick = () => setIsRejectModalOpen(true);

  // Approve
  const confirmApprove = async () => {
    if (!orderId) return;
    try {
      await approveOrder(orderId).unwrap();
      addToast({
        title: 'Success!',
        description: 'Order approved successfully!',
        color: 'success',
        severity: 'success',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
      setIsApproveModalOpen(false);
      onCloseModal();
      onActionComplete();
    } catch (e) {
      console.error(e);
      addToast({
        title: 'Error!',
        description: 'Failed to approve order!',
        color: 'danger',
        severity: 'danger',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
    }
  };

  // Reject
  const confirmReject = async () => {
    if (!orderId) return;
    try {
      await rejectOrder({ orderId, rejectionReason: rejectReason }).unwrap();
      addToast({
        title: 'Success!',
        description: 'Order rejected successfully!',
        color: 'success',
        severity: 'success',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
      setIsRejectModalOpen(false);
      onCloseModal();
      onActionComplete();
    } catch (e) {
      console.error(e);
      addToast({
        title: 'Error!',
        description: 'Failed to reject order!',
        color: 'danger',
        severity: 'danger',
        variant: 'bordered',
        radius: 'lg',
        timeout: 3000,
        shouldShowTimeoutProgress: true,
      });
    }
  };

  const tableClassNames = useMemo(
    () => ({
      th: ['bg-[var(--bg-surface-2)]', 'border-b', 'border-[var(--border-light)]', 'text-sm', 'p-2', 'text-[var(--text-muted)]'],
      td: ['text-md', 'p-2', 'text-[var(--text-main)]'],
    }),
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner
          className="text-[var(--color-primary)]"
          label="Loading order details..."
          variant="spinner"
          size="lg"
          classNames={{ label: 'text-[var(--text-main)] mt-2 opacity-75' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-[var(--text-main)]">Error loading order. Please try again.</div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return moment(dateString).format('L LT');
  };

  return (
    <div className="p-4 text-[var(--text-main)] space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1 text-[var(--text-main)]">{order.orderNumber}</h1>
        <Chip
          color="default"
          className={`capitalize px-3 py-1 text-sm font-medium rounded-lg shadow-xl ${getStatusChipStyle(
            order.status
          )}`}
        >
          {formatText(order.status)}
        </Chip>
        <p className="text-[var(--text-muted)] text-sm mt-2 opacity-75">
          Created date: {formatDate(order.createdAt)}
        </p>
      </div>

      <Divider className="bg-[var(--border-light)] opacity-20" />

      <div className="flex gap-24 items-center">
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.order_type')}
          </label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">{order.orderType}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.application')}
          </label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">{order.application}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.event_date')}
          </label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">
              {formatDate(order.eventDate)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.valid_from')}
          </label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">
              {formatDate(order.validFrom)}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">{t('orders_page.valid_to')}</label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">{formatDate(order.validTo)}</p>
          </div>
        </div>
      </div>

      <Divider className="bg-[var(--border-light)] opacity-20" />

      <div className="flex gap-24 items-center">
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">{t('orders_page.trips')}</label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">{order.trips}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">{t('orders_page.origin')}</label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">{order.address?.origin}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.destination')}
          </label>
          <div className="flex items-center gap-4">
            <p className="text-[var(--text-main)] capitalize">
              {order.address?.destination}
            </p>
          </div>
        </div>
      </div>

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.shippingCompany && (
        <div className="flex gap-24 items-center">
          <div className="flex flex-col gap-2">
            <label className="text-[var(--text-muted)]">
              {t('orders_page.shipping_company')}
            </label>
            <div className="flex items-center gap-4">
              <p className="text-[var(--text-main)] capitalize">
                {order.shippingCompany.name}
              </p>
              <PdfViewer
                filePath={order.shippingCompany.tradeLicenseFilePath}
                fileLabel="Event Layout File"
                label="View Trade Licence"
                size="sm"
              />
            </div>
          </div>
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.shipments && order.shipments.length > 0 && (
        <div className="space-y-8">
          <label className="text-[var(--text-muted)] text-base font-semibold">
            {t('orders_page.shipment_details', {
              defaultValue: 'Shipment Details',
            })}
          </label>

          {order.shipments.map((shipment: any, index: number) => (
            <div
              key={shipment._id}
              className="p-4 rounded-md space-y-2 border shadow-sm" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-light)" }}
            >
              <h3 className="text-[var(--text-main)] font-bold text-lg mb-2">
                Shipment {index + 1}
              </h3>

              <Table
                aria-label={`shipment-${index + 1}`}
                className="" style={{ backgroundColor: "var(--bg-surface)" }}
                isCompact
                removeWrapper
                classNames={tableClassNames}
              >
                <TableHeader>
                  <TableColumn>Description</TableColumn>
                  <TableColumn>UN SL No</TableColumn>
                  <TableColumn>HD</TableColumn>
                  <TableColumn>Boxes Qty</TableColumn>
                  <TableColumn>Net Weight</TableColumn>
                  <TableColumn>Gross Weight</TableColumn>
                </TableHeader>
                <TableBody>
                  {shipment.shipmentDetails.map((detail: any, i: number) => (
                    <TableRow key={detail._id || i}>
                      <TableCell>{detail.description}</TableCell>
                      <TableCell>{detail.unSlNo}</TableCell>
                      <TableCell>{detail.hd}</TableCell>
                      <TableCell>{detail.boxesQty}</TableCell>
                      <TableCell>{detail.netWeight}</TableCell>
                      <TableCell>{detail.grossWeight}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      <div className="flex gap-24 items-center">
        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.event_layout_file', {
              defaultValue: 'Event Layout File',
            })}
          </label>
          <div className="flex items-center gap-4">
            <PdfViewer
              filePath={order.eventLayoutFilePath}
              fileLabel="Event Layout File"
              size="sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.event_letter_file', {
              defaultValue: 'Event Letter File',
            })}
          </label>
          <div className="flex items-center gap-4">
            <PdfViewer
              filePath={order.eventLetterFilePath}
              fileLabel="Event Letter File"
              size="sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[var(--text-muted)]">
            {t('orders_page.sira_approval_file', {
              defaultValue: 'Sira Approval File',
            })}
          </label>
          <div className="flex items-center gap-4">
            <PdfViewer
              filePath={order.siraApprovalFilePath}
              fileLabel="Sira Approval File"
              size="sm"
            />
          </div>
        </div>
      </div>

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.possibleVehicles && order.possibleVehicles.length > 0 && (
        <div className="flex gap-24 items-center">
          <div className="flex flex-col gap-2">
            <label className="text-[var(--text-muted)]">
              {t('orders_page.vehicles', {
                defaultValue: 'Vehicles',
              })}
            </label>
            <div className="flex flex-wrap gap-4">
              {order.possibleVehicles.map((vehicle: any) => (
                <Chip
                  key={vehicle._id}
                  className="py-4 bg-[var(--bg-surface-2)] text-[var(--text-main)] capitalize rounded-lg border border-[var(--border-light)] shadow-lg"
                  variant="flat"
                >
                  {vehicle.plateNumber}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.poc && (
        <div className="flex gap-24 items-center">
          <div className="flex flex-col gap-2">
            <label className="text-[var(--text-muted)]">
              {t('orders_page.poc', {
                defaultValue: 'Point of Contact',
              })}
            </label>
            <div className="flex items-center gap-4 p-3 bg-[var(--bg-surface-2)] border border-[var(--border-light)] shadow-lg rounded-lg min-w-52">
              <User
                avatarProps={{
                  src: '/assets/icons/vendors/avatar.svg',
                  className: 'border border-[var(--border-light)]',
                }}
                description={<span className="text-[var(--text-muted)]">{order.poc.idNumber}</span>}
                name={<span className="text-[var(--text-main)] font-semibold">{`${order.poc.firstName} ${order.poc.lastName}`}</span>}
              />
              {order.poc.siraIdFilePath && (
                <PdfViewer
                  filePath={order.poc.siraIdFilePath}
                  fileLabel="SIRA ID"
                  label="View ID"
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.possibleDrivers && order.possibleDrivers.length > 0 && (
        <div className="flex gap-24 items-center">
          <div className="flex flex-col gap-2">
            <label className="text-[var(--text-muted)]">
              {t('orders_page.drivers', {
                defaultValue: 'Drivers',
              })}
            </label>
            <div className="flex flex-wrap gap-4">
              {order.possibleDrivers.map((driver: any) => (
                <div key={driver._id} className="flex items-center gap-4 p-3 bg-[var(--bg-surface-2)] border border-[var(--border-light)] shadow-lg rounded-lg min-w-52">
                  <User
                    avatarProps={{
                      src: '/assets/icons/vendors/avatar.svg',
                      className: 'border border-[var(--border-light)]',
                    }}
                    description={<span className="text-[var(--text-muted)]">{driver.dlNumber}</span>}
                    name={<span className="text-[var(--text-main)] font-semibold">{`${driver.firstName} ${driver.lastName}`}</span>}
                  />
                  {driver.dlFilePath && (
                    <PdfViewer
                      filePath={driver.dlFilePath}
                      fileLabel="Driving License"
                      label="View ID"
                      size="sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.possibleEscorts && order.possibleEscorts.length > 0 && (
        <div className="flex gap-24 items-center">
          <div className="flex flex-col gap-2">
            <label className="text-[var(--text-muted)]">
              {t('orders_page.escorts', {
                defaultValue: 'Escorts',
              })}
            </label>
            <div className="flex flex-wrap gap-4">
              {order.possibleEscorts.map((escort: any) => (
                <div key={escort._id} className="flex items-center gap-4 p-3 bg-[var(--bg-surface-2)] border border-[var(--border-light)] shadow-lg rounded-lg min-w-44">
                  <User
                    avatarProps={{
                      src: '/assets/icons/vendors/avatar.svg',
                      className: 'border border-[var(--border-light)]',
                    }}
                    description={<span className="text-[var(--text-muted)]">{escort.dlNumber}</span>}
                    name={<span className="text-[var(--text-main)] font-semibold">{`${escort.firstName} ${escort.lastName}`}</span>}
                  />
                  {escort.siraIdFilePath && (
                    <PdfViewer
                      filePath={escort.siraIdFilePath}
                      fileLabel="Driving License"
                      label="View ID"
                      size="sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Divider className="bg-[var(--border-light)] opacity-20" />

      {order.status === 'pending_approval' && (
        <div className="flex justify-end gap-6">
          <Button
            variant="solid"
            className="mt-8 font-semibold shadow-lg px-8 py-2 rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
            onClick={handleApproveClick}
            isDisabled={isApproving || isRejecting}
          >
            {isApproving ? 'Approving...' : 'Accept Order'}
          </Button>
          <Button
            variant="bordered"
            className="mt-8 font-semibold shadow-lg px-8 py-2 rounded-lg border-[var(--color-error)] text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white transition-all"
            onClick={handleRejectClick}
            isDisabled={isApproving || isRejecting}
          >
            {isRejecting ? 'Rejecting...' : 'Reject Order'}
          </Button>
        </div>
      )}

      {/* Confirm Approve Modal */}
      <Modal
        size="md"
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        backdrop="blur"
        classNames={{
          closeButton: 'bg-[var(--bg-overlay)] m-1 hover:bg-[var(--bg-surface-2)] text-[var(--text-main)] p-2',
        }}
      >
        <ModalContent className="bg-[var(--bg-surface)] border border-[var(--border-light)]">
          {() => (
            <>
              <ModalHeader className="text-[var(--text-main)] font-bold text-2xl mt-1 ps-8">
                {t('orders_page.confirm_approval', {
                  defaultValue: 'Confirm Approval',
                })}
              </ModalHeader>
              <ModalBody className="overflow-y-auto">
                <p className="text-[var(--text-main)]">
                  {t('orders_page.approval_question', {
                    defaultValue: 'Are you sure you want to approve this order?',
                  })}
                </p>
              </ModalBody>
              <ModalFooter className="flex justify-end gap-4">
                <Button
                  variant="flat"
                  onPress={() => setIsApproveModalOpen(false)}
                  className="text-[var(--text-muted)] border border-[var(--border-light)]"
                >
                  {t('button.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button
                  variant="solid"
                  onPress={confirmApprove}
                  isDisabled={isApproving || isRejecting}
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                >
                  {t('button.confirm', { defaultValue: 'Confirm' })}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confirm Reject Modal (with reason) */}
      <Modal
        size="md"
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        backdrop="blur"
        classNames={{
          closeButton: 'bg-[var(--bg-overlay)] m-1 hover:bg-[var(--bg-surface-2)] text-[var(--text-main)] p-2',
        }}
      >
        <ModalContent className="bg-[var(--bg-surface)] border border-[var(--border-light)]">
          {() => (
            <>
              <ModalHeader className="text-[var(--text-main)] font-bold text-2xl mt-1 ps-8">
                {t('orders_page.confirm_rejection', {
                  defaultValue: 'Confirm Rejection',
                })}
              </ModalHeader>
              <ModalBody className="overflow-y-auto flex flex-col gap-4">
                <p className="text-[var(--text-main)]">
                  {t('orders_page.rejection_question', {
                    defaultValue: 'Are you sure you want to reject this order?',
                  })}
                </p>
                <textarea
                  className="w-full h-24 p-2 bg-[var(--bg-surface-2)] border border-[var(--border-light)] text-[var(--text-main)] rounded-md focus:outline-none placeholder:text-[var(--text-muted)]"
                  placeholder={t('orders_page.reject_reason_placeholder', {
                    defaultValue: 'Please provide a reason for rejection...',
                  })}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </ModalBody>
              <ModalFooter className="flex justify-end gap-4">
                <Button
                  variant="flat"
                  onPress={() => setIsRejectModalOpen(false)}
                  className="text-[var(--text-muted)] border border-[var(--border-light)]"
                >
                  {t('button.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button
                  variant="solid"
                  className="bg-[var(--color-error)] text-white"
                  onPress={confirmReject}
                  isDisabled={isApproving || isRejecting || !rejectReason.trim()}
                >
                  {t('button.confirm', { defaultValue: 'Confirm' })}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ReviewOrder;
